import React, { useEffect, useState } from 'react';
import { Accessibility, ClipboardPlus, HandCoins, Hospital, WalletCards, MessageSquareShare, Mouse, ReceiptText, Syringe, UsersRound, Save, Loader, SquarePen, X } from 'lucide-react';
import { can_visit, getPractice, notify, numberWithCommas } from '../../../helpers';
import { potentialRevenue, updatePotentialRevenue } from '../../../services/audits';
import { useDocumentTitle } from '../../../hooks/customDocumentTitleHook';
import Card from '../../Card';
import Grid from '../../Grid';
import Spacing from '../../Spacing';
import PrecisionCodingBillingModal from '../../Modal/Audits/PrecisionCodingBillingModal';
import PatientRetentionModal from '../../Modal/Audits/PatientRetentionModal';
import ChronicCareManagementModal from '../../Modal/Audits/ChronicCareManagementModal';
import QualityIncentivePaymentModal from '../../Modal/Audits/QualityIncentivePaymentModal';
import MaximizingPreventiveCareModal from '../../Modal/Audits/MaximizingPreventiveCareModal';
import TransitionalCareManagementModal from '../../Modal/Audits/TransitionalCareManagementModal';

export default function AuditReports({selectedPracticeChange, fetchChangePracticeData}) {
  let account_id = getPractice("account_id");
  let location_id = getPractice("location_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    account_id = selectedPracticeChange?.account_id;
    location_id = selectedPracticeChange?.location_id;
  }
  const [loadData, setLoadData] = useState(true);
  const [practicePotentialRevenue, setPracticePotentialRevenue] = useState([]);
  const [precisionCodingBillingModalOpen, setPrecisionCodingBillingModalOpen] = useState(false);
  const [patientRetentionModalOpen, setPatientRetentionModalOpen] = useState(false);
  const [chronicCareManagementModalOpen, setChronicCareManagementModalOpen] = useState(false);
  const [qualityIncentivePaymentModalOpen, setQualityIncentivePaymentModalOpen] = useState(false);
  const [maximizingPreventiveCareModalOpen, setMaximizingPreventiveCareModalOpen] = useState(false);
  const [transitionalCareManagementModalOpen, setTransitionalCareManagementModalOpen] = useState(false);
  const [saveAuditLoader, setSaveAuditLoader] = useState(false);
  const [editAuditReport, setEditAuditReport] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    getPotentialRevenue(account_id, location_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id, location_id]);

  const getPotentialRevenue = async (accountId, locationId) => {
    try {
      const response = await potentialRevenue(accountId, locationId);

      if (response) {
        if(response?.detail) {
          notify("error", response?.detail);
        } else {
          setPracticePotentialRevenue(response?.data);
          setAuditFormData(response?.data);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ getPotentialRevenue ~ error:", error);
    } finally {
      setLoadData(false);
    }
  };

  const setAuditFormData = (data) => {
    const initialData = {
      account_id: account_id,
      location_id: location_id,
      revenue_missed: data?.revenue_missed,
      total_patients: data?.total_patients,
      high_risk_patients: data?.high_risk_patients,
      per_patient_encounter: data?.per_patient_encounter,
    };

    data?.potential_revenue?.forEach((item) => {
      const key = item?.name?.replace(/\s+/g, "_")?.toLowerCase();
      initialData[key] = item?.revenue_missed;
    });

    setFormData(initialData);
  };

  // Calculate total revenue missed
  const calculateTotalRevenueMissed = () => {
    return practicePotentialRevenue.potential_revenue.reduce((total, item) => {
      const key = item.name.replace(/\s+/g, "_").toLowerCase();
      return total + (formData[key] || 0);
    }, 0);
  };

  const viewPotentialRevenue = (index) => {
    if(index === 0) {
      setPrecisionCodingBillingModalOpen(true);
    } else if(index === 1) {
      setPatientRetentionModalOpen(true);
    } else if(index === 2) {
      setChronicCareManagementModalOpen(true);
    } else if(index === 3) {
      setQualityIncentivePaymentModalOpen(true);
    } else if(index === 4) {
      setMaximizingPreventiveCareModalOpen(true);
    } else if(index === 5) {
      setTransitionalCareManagementModalOpen(true);
    }
  }; 

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value === "" ? 0 : parseFloat(value);

    setFormData((prevData) => ({
      ...prevData,
      [name]: numericValue,
    }));
  };

  const updateAuditReport = async (e) => {
    e.preventDefault();
    setSaveAuditLoader(true);

    try {
      const response = await updatePotentialRevenue(formData);
      if(response){
        if(response?.detail) {
          notify("error", response?.detail);
        } else {
          if(response?.data?.status === 'success') {
            notify("success", response?.data?.message);
            await getPotentialRevenue(account_id, location_id);
            setEditAuditReport(false);
          } else  {
            notify("error", response?.data?.message);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 1);
      }
    } finally {
      setSaveAuditLoader(false);
    }
  }

  useDocumentTitle("Audit");

  return (
    <>
      <form className="relative" onSubmit={updateAuditReport}>
        <Grid variant="docpro_grid_gap_20 docpro_grid_col_4">
          <div className="docpro_card docpro_card_md docpro_card_1">
            <h3 className="docpro_fs_14 docpro_mb_0 docpro_olive_color">
              <WalletCards /> 
              Total revenue missed
            </h3>
            <Spacing lg="5" sm="5" />
            <p className="docpro_fw_500 docpro_fs_28 docpro_primary_color docpro_mb_0">
              {!editAuditReport ? 
                <>${practicePotentialRevenue?.revenue_missed ? numberWithCommas(practicePotentialRevenue?.revenue_missed) : 0.00}</>
              :
                <span className="docpro_signout_input items-center">
                  $
                  <input
                    type="text"
                    className="docpro_fw_500"
                    style={{ maxWidth: "170px" }}
                    name="revenue_missed"
                    defaultValue={formData?.revenue_missed || 0}
                    onChange={handleInputChange}
                  />
                </span>
              }
            </p>
          </div>
          <div className="docpro_card docpro_card_md docpro_card_1">
            <h3 className="docpro_fs_14 docpro_mb_0 docpro_olive_color">
              <UsersRound /> 
              Total number Of patients
            </h3>
            <Spacing lg="5" sm="5" />
            <p className="docpro_fw_500 docpro_fs_28 docpro_primary_color docpro_mb_0">
              {!editAuditReport ? 
                <>{practicePotentialRevenue?.total_patients ? numberWithCommas(practicePotentialRevenue?.total_patients) : 0}</>
              :
                <span className="docpro_signout_input">
                  <input
                    type="text"
                    className="docpro_fw_500"
                    style={{ maxWidth: "150px" }}
                    name="total_patients"
                    defaultValue={formData?.total_patients || 0}
                    onChange={handleInputChange}
                  />
                </span>
              }
            </p>
          </div>
          <div className="docpro_card docpro_card_md docpro_card_1">
            <h3 className="docpro_fs_14 docpro_mb_0 docpro_olive_color">
              <Accessibility /> 
              High risk patients
            </h3>
            <Spacing lg="5" sm="5" />
            <p className="docpro_fw_500 docpro_fs_28 docpro_primary_color docpro_mb_0">
              {!editAuditReport ? 
                <>{practicePotentialRevenue?.high_risk_patients ? numberWithCommas(practicePotentialRevenue?.high_risk_patients) : 0}</>
              :
                <span className="docpro_signout_input">
                  <input
                    type="text"
                    className="docpro_fw_500"
                    style={{ maxWidth: "150px" }}
                    name="high_risk_patients"
                    defaultValue={formData?.high_risk_patients || 0}
                    onChange={handleInputChange}
                  />
                </span>
              }
            </p>
          </div>
          <div className="docpro_card docpro_card_md docpro_card_1">
            <h3 className="docpro_fs_14 docpro_mb_0 docpro_olive_color">
              <WalletCards /> 
              Revenue per encounter
            </h3>
            <Spacing lg="5" sm="5" />
            <p className="docpro_fw_500 docpro_fs_28 docpro_primary_color docpro_mb_0">
              {!editAuditReport ? 
                <>${practicePotentialRevenue?.per_patient_encounter ? numberWithCommas(practicePotentialRevenue?.per_patient_encounter) : 0.00}</>
              :
                <span className="docpro_signout_input items-center">
                  $
                  <input
                    type="text"
                    className="docpro_fw_500"
                    style={{ maxWidth: "150px" }}
                    name="per_patient_encounter"
                    defaultValue={formData?.per_patient_encounter || 0}
                    onChange={handleInputChange}
                  />
                </span>
              }
            </p>
          </div>
        </Grid>
        <Spacing lg="25" sm="25" />

        <Card variant="docpro_card docpro_card_1 p-0">
          <div className='docpro_table_responsive'>
            <table className="docpro_audit_table docpro_mb_0">
              <tbody>
                <tr>
                  <td colSpan="2">
                    <Spacing lg="10" sm="10" />
                  </td>
                </tr>
                <tr>
                  <td>
                    <div className='mb-2 pl-2'>
                      <h3 className='docpro_fs_20 docpro_mb_0 mb-1'>Potential revenue</h3>
                      <p className='docpro_fs_14 docpro_mb_0 flex items-center'><Mouse width={16} height={16} className='docpro_olive_color' /> Click on each categories to see detailed report</p>
                    </div>
                  </td>
                  <td className='missed_td'>
                    <h4 className='docpro_fs_16 docpro_fw_600 docpro_mb_0 text-right pr-2'>Revenue missed</h4>
                  </td>
                </tr>
                {!loadData ? (
                  practicePotentialRevenue?.potential_revenue?.length > 0 ? (
                    <>
                      {practicePotentialRevenue.potential_revenue && practicePotentialRevenue.potential_revenue.map((item, index) => {
                        const key = item.name.replace(/\s+/g, "_").toLowerCase();
                        return (
                          <tr key={index}>
                            <td>
                              <div className="docpro_audit_progress pl-2">
                                <div className={`docpro_audit_progress_in`} onClick={() => viewPotentialRevenue(index)}>
                                  <div className={`docpro_secondary_color docpro_audit_progress_in_${index+1} progress`}>
                                    <h2 className="docpro_page_heading docpro_fs_16 docpro_fw_500 docpro_mb_0">
                                      <i className="docpro_white_color icon">
                                        {index === 0 && (
                                          <ReceiptText width={18} height={18} />
                                        )}
                                        {index === 1 && (
                                          <MessageSquareShare width={18} height={18} />
                                        )}
                                        {index === 2 && (
                                          <ClipboardPlus width={18} height={18} />
                                        )}
                                        {index === 3 && (
                                          <HandCoins width={18} height={18} />
                                        )}
                                        {index === 4 && (
                                          <Syringe width={18} height={18} />
                                        )}
                                        {index === 5 && (
                                          <Hospital width={18} height={18} />
                                        )}
                                      </i>
                                      {item?.name}
                                    </h2>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <h4 className='docpro_fs_16 docpro_secondary_color docpro_fw_500 docpro_mb_0 text-right pr-2'>
                                {!editAuditReport ? 
                                  <>${numberWithCommas(item?.revenue_missed || 0.00)}</>
                                :
                                  <span className="docpro_signout_input items-center" style={{ justifyContent: "end" }}>
                                    $
                                    <input
                                      type="text"
                                      className="docpro_fw_500 text-right"
                                      style={{ maxWidth: "130px" }}
                                      name={key}
                                      defaultValue={formData[key] || 0}
                                      onChange={handleInputChange}
                                    />
                                  </span>
                                }
                              </h4>
                            </td>
                          </tr>
                        );
                      })}

                      <tr>
                        <td colSpan="2">
                          <Spacing lg="6" sm="6" />
                        </td>
                      </tr>
                      <tr>
                        <td style={{ borderTop: '1px solid var(--border)' }}>
                          <h4 className='docpro_fs_16 docpro_fw_400 docpro_mb_0 docpro_secondary_color mt-2 mb-2 pl-2'>Total revenue missed</h4>
                        </td>
                        <td style={{ borderTop: '1px solid var(--border)' }}>
                          <h4 className='docpro_fs_16 docpro_fw_700 docpro_mb_0 text-right docpro_primary_color mt-2 mb-2 pr-2'>${numberWithCommas(calculateTotalRevenueMissed())}</h4>
                        </td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td className="no_data" colSpan="2">
                        No data available
                      </td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td className="no_data" colSpan="2">
                      Loading...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {can_visit('administrator') && (
          <div className="absolute" style={{ top: '-15px', right: '-10px' }}>
            {!editAuditReport ?
              <button 
                type="button"
                className="docpro_bordered_btn" 
                style={{ background: '#1578d5', borderRadius: '5px', color: '#ffffff' }}
                onClick={()=>{setEditAuditReport(true);}}
              >
                <SquarePen width={20} height={20} />
                <span>Edit</span>
              </button>
            :
              <div className="flex item-center docpro_grid_gap_10">
                <button 
                  type="button"
                  className="docpro_bordered_btn" 
                  style={{ background: '#e5484d', borderRadius: '5px', color: '#ffffff' }}
                  onClick={()=>{
                    setEditAuditReport(false);
                  }}
                >
                  <X width={20} height={20} />
                  <span>Cancel</span>
                </button>
                {editAuditReport && (
                  <button
                    type="submit"
                    style={{ borderRadius: '4px' }}
                    className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
                    disabled={saveAuditLoader ? true : false}
                  >
                    <Save width={18} height={18} />
                    <span>Save</span>
                    {saveAuditLoader && (
                      <span className="docpro_loading">
                        <Loader width={20} height={20} />
                      </span>
                    )}
                  </button>
                )}
              </div>
            }
          </div>
        )}
      </form>

      {precisionCodingBillingModalOpen && (
        <PrecisionCodingBillingModal
          account_id={account_id}
          location_id={location_id}
          activeModal={precisionCodingBillingModalOpen}
          setActiveModal={setPrecisionCodingBillingModalOpen}
        />
      )}

      {patientRetentionModalOpen && (
        <PatientRetentionModal
          account_id={account_id}
          location_id={location_id}
          activeModal={patientRetentionModalOpen}
          setActiveModal={setPatientRetentionModalOpen}
        />
      )}

      {chronicCareManagementModalOpen && (
        <ChronicCareManagementModal
          account_id={account_id}
          location_id={location_id}
          activeModal={chronicCareManagementModalOpen}
          setActiveModal={setChronicCareManagementModalOpen}
        />
      )}

      {qualityIncentivePaymentModalOpen && (
        <QualityIncentivePaymentModal
          account_id={account_id}
          location_id={location_id}
          activeModal={qualityIncentivePaymentModalOpen}
          setActiveModal={setQualityIncentivePaymentModalOpen}
        />
      )}

      {maximizingPreventiveCareModalOpen && (
        <MaximizingPreventiveCareModal
          account_id={account_id}
          location_id={location_id}
          activeModal={maximizingPreventiveCareModalOpen}
          setActiveModal={setMaximizingPreventiveCareModalOpen}
        />
      )}

      {transitionalCareManagementModalOpen && (
        <TransitionalCareManagementModal
          account_id={account_id}
          location_id={location_id}
          activeModal={transitionalCareManagementModalOpen}
          setActiveModal={setTransitionalCareManagementModalOpen}
        />
      )}
    </>
  );
}
